/*
 * B2B inquiry demo: shared behaviour.
 * - Inquiry modal (native <dialog>) + prefill from [data-b2b-inquiry-open]
 * - Contact-form submit: reference number, item summary, success handling
 * - Inquiry list page (/cart): qty change / remove via /cart/change.js
 * - Product page: gallery, quantity stepper, tabs, sticky bar
 * - Product hotspots
 * Everything uses event delegation so it keeps working after theme-editor section reloads.
 */
(() => {
  if (window.__b2bInit) return;
  window.__b2bInit = true;

  const STORAGE_KEY = "b2bLastInquiry";
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const root = () => window.Shopify?.routes?.root || "/";

  const makeRef = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
    return `NJ-${String(d.getFullYear()).slice(2)}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${rand}`;
  };

  const refreshMiniCart = () => {
    const mini = document.querySelector("mini-cart");
    if (mini && typeof mini.updateSectionContents === "function") {
      mini.updateSectionContents().catch(() => {});
    }
  };

  /* ---------------- Inquiry modal ---------------- */
  const modal = () => document.getElementById("B2BInquiryModal");

  const setField = (scope, name, value) => {
    const el = scope.querySelector(`[data-b2b-field="${name}"]`);
    if (el) el.value = value || "";
  };

  const openInquiry = (trigger) => {
    const dialog = modal();
    if (!dialog) {
      // Inquiry list page: there is no modal, jump to the form instead.
      const form = document.getElementById("b2b-cart-form");
      if (form) {
        form.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => form.querySelector(".b2b-cart-panel input:not([type=hidden])")?.focus({ preventScroll: true }), 400);
        return true;
      }
      return false;
    }

    const form = dialog.querySelector("form");
    const product = trigger?.dataset.b2bProduct || "";
    const sku = trigger?.dataset.b2bSku || "";
    let qty = "";
    if (trigger?.dataset.b2bQtyFrom) {
      qty = document.querySelector(trigger.dataset.b2bQtyFrom)?.value || "";
    }

    if (trigger && form && !dialog.querySelector("[data-b2b-success]")) {
      setField(form, "product", product);
      setField(form, "sku", sku);
      setField(form, "qty", qty);
      setField(form, "source", trigger.dataset.b2bSource || "Website");
      setField(form, "url", window.location.href.split("?")[0]);

      const label = dialog.querySelector("[data-b2b-product-label]");
      if (label) {
        label.hidden = !product;
        label.textContent = product ? `${product}${sku ? ` · Model ${sku}` : ""}` : "";
      }
    }

    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add("b2b-modal-open");
    const first = dialog.querySelector("[data-b2b-success] button, .b2b-form-grid input");
    setTimeout(() => first?.focus(), 50);
    return true;
  };

  const closeInquiry = () => {
    const dialog = modal();
    if (dialog?.open) dialog.close();
  };

  document.addEventListener("click", (event) => {
    const opener = event.target.closest("[data-b2b-inquiry-open]");
    if (opener) {
      if (openInquiry(opener)) event.preventDefault();
      return;
    }
    if (event.target.closest("[data-b2b-inquiry-close]")) {
      event.preventDefault();
      closeInquiry();
      return;
    }
    // Click on the dialog backdrop.
    const dialog = modal();
    if (dialog && event.target === dialog) closeInquiry();
  });

  document.addEventListener(
    "close",
    (event) => {
      if (event.target === modal()) document.documentElement.classList.remove("b2b-modal-open");
    },
    true,
  );

  /* ---------------- Contact forms ---------------- */
  const buildItemsSummary = () => {
    const lines = $$("[data-b2b-line]");
    if (!lines.length) return { text: "(no products, general inquiry)", units: 0 };
    let units = 0;
    const text = lines
      .map((line, i) => {
        const qty = parseInt($("[data-b2b-line-qty]", line)?.value || "1", 10);
        units += qty;
        const sku = line.dataset.sku ? ` [${line.dataset.sku}]` : "";
        return `${i + 1}. ${line.dataset.title}${sku} x ${qty}`;
      })
      .join("; ");
    return { text, units };
  };

  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains("b2b-form")) return;

    const ref = makeRef();
    setField(form, "ref", ref);

    const isCart = form.id === "b2b-cart-form";
    if (isCart) {
      const { text, units } = buildItemsSummary();
      setField(form, "items", text);
      setField(form, "units", String(units));
    }

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ref, form: isCart ? "cart" : "modal", at: Date.now() }));
    } catch (e) {
      /* storage unavailable */
    }

    const button = form.querySelector('[type="submit"]');
    if (button) {
      button.setAttribute("aria-disabled", "true");
      button.classList.add("loading");
    }
  });

  const handlePosted = () => {
    let last = null;
    try {
      last = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
    } catch (e) {
      last = null;
    }
    const ref = last?.ref || makeRef();
    $$("[data-b2b-ref]").forEach((el) => (el.textContent = ref));

    const success = $("[data-b2b-success]");
    const errors = $("[data-b2b-form-errors]");

    if ((success && success.closest("#B2BInquiryModal")) || (errors && errors.closest("#B2BInquiryModal"))) {
      openInquiry(null);
    }

    // Inquiry list submitted: clear the list (the server-rendered summary stays on screen).
    const clearedKey = `${STORAGE_KEY}:cleared:${ref}`;
    if (success && success.hasAttribute("data-b2b-clear-cart") && !sessionStorage.getItem(clearedKey)) {
      fetch(`${root()}cart/clear.js`, { method: "POST", headers: { Accept: "application/json" } })
        .then(() => {
          try {
            sessionStorage.setItem(clearedKey, "1");
          } catch (e) {
            /* ignore */
          }
          refreshMiniCart();
        })
        .catch(() => {});
    }
  };

  /* ---------------- Quantity steppers ---------------- */
  document.addEventListener("click", (event) => {
    const step = event.target.closest("[data-b2b-qty-step]");
    if (!step) return;
    const input = step.closest("[data-b2b-qty]")?.querySelector("input");
    if (!input) return;
    const min = parseInt(input.min || "1", 10);
    const next = Math.max(min, (parseInt(input.value, 10) || min) + parseInt(step.dataset.b2bQtyStep, 10));
    if (String(next) !== input.value) {
      input.value = next;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });

  /* ---------------- Inquiry list page ---------------- */
  const timers = new WeakMap();
  const changeLine = (line, quantity) => {
    line.classList.add("is-loading");
    return fetch(`${root()}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: line.dataset.key, quantity }),
    })
      .then((r) => r.json())
      .then((cart) => {
        line.classList.remove("is-loading");
        if (cart.status) throw new Error(cart.description || cart.message);
        if (quantity === 0) line.remove();
        const count = $("[data-b2b-cart-count]");
        if (count) count.textContent = cart.item_count;
        setField(document, "units", String(cart.item_count));
        if (!$$("[data-b2b-line]").length) {
          $("[data-b2b-lines]")?.remove();
          const empty = $("[data-b2b-cart-empty]");
          if (empty) empty.hidden = false;
        }
        refreshMiniCart();
      })
      .catch((err) => {
        line.classList.remove("is-loading");
        // eslint-disable-next-line no-alert
        window.alert(err.message || "Could not update the inquiry list, please try again.");
      });
  };

  document.addEventListener("change", (event) => {
    const input = event.target.closest("[data-b2b-line-qty]");
    if (!input) return;
    const line = input.closest("[data-b2b-line]");
    const qty = Math.max(1, parseInt(input.value, 10) || 1);
    input.value = qty;
    clearTimeout(timers.get(line));
    timers.set(line, setTimeout(() => changeLine(line, qty), 350));
  });

  document.addEventListener("keydown", (event) => {
    // Enter in a quantity field must not submit the inquiry form.
    if (event.key === "Enter" && event.target.closest?.("[data-b2b-line-qty]")) {
      event.preventDefault();
      event.target.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });

  document.addEventListener("click", (event) => {
    const remove = event.target.closest("[data-b2b-line-remove]");
    if (!remove) return;
    event.preventDefault();
    changeLine(remove.closest("[data-b2b-line]"), 0);
  });

  /* ---------------- Gallery ---------------- */
  const galleryIndex = (track) => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));

  const goToSlide = (gallery, index) => {
    const track = $("[data-b2b-gallery-track]", gallery);
    const slides = $$(".b2b-gallery-slide", track);
    if (!slides.length) return;
    const i = (index + slides.length) % slides.length;
    track.scrollTo({ left: i * track.clientWidth, behavior: "smooth" });
  };

  document.addEventListener("click", (event) => {
    const gallery = event.target.closest("[data-b2b-gallery]");
    if (!gallery) return;
    const track = $("[data-b2b-gallery-track]", gallery);
    const thumb = event.target.closest("[data-b2b-gallery-thumb]");
    if (thumb) goToSlide(gallery, parseInt(thumb.dataset.b2bGalleryThumb, 10));
    if (event.target.closest("[data-b2b-gallery-prev]")) goToSlide(gallery, galleryIndex(track) - 1);
    if (event.target.closest("[data-b2b-gallery-next]")) goToSlide(gallery, galleryIndex(track) + 1);
  });

  document.addEventListener(
    "scroll",
    (event) => {
      const track = event.target;
      if (!(track instanceof HTMLElement) || !track.matches("[data-b2b-gallery-track]")) return;
      const gallery = track.closest("[data-b2b-gallery]");
      const i = galleryIndex(track);
      const current = $("[data-b2b-gallery-current]", gallery);
      if (current) current.textContent = i + 1;
      $$("[data-b2b-gallery-thumb]", gallery).forEach((t, n) => {
        if (n === i) {
          t.setAttribute("aria-current", "true");
          t.scrollIntoView({ block: "nearest", inline: "nearest" });
        } else t.removeAttribute("aria-current");
      });
    },
    true,
  );

  document.addEventListener("keydown", (event) => {
    const track = event.target.closest?.("[data-b2b-gallery-track]");
    if (!track || (event.key !== "ArrowLeft" && event.key !== "ArrowRight")) return;
    event.preventDefault();
    goToSlide(track.closest("[data-b2b-gallery]"), galleryIndex(track) + (event.key === "ArrowRight" ? 1 : -1));
  });

  /* ---------------- Tabs ---------------- */
  const selectTab = (tab) => {
    const list = tab.closest("[role=tablist]");
    const scope = tab.closest("[data-b2b-tabs]");
    $$("[role=tab]", list).forEach((t) => {
      const selected = t === tab;
      t.setAttribute("aria-selected", String(selected));
      t.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(t.getAttribute("aria-controls"));
      if (panel && scope.contains(panel)) panel.hidden = !selected;
    });
  };

  document.addEventListener("click", (event) => {
    const tab = event.target.closest("[data-b2b-tabs] [role=tab]");
    if (tab) selectTab(tab);
  });

  document.addEventListener("keydown", (event) => {
    const tab = event.target.closest?.("[data-b2b-tabs] [role=tab]");
    if (!tab || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    const tabs = $$("[role=tab]", tab.closest("[role=tablist]"));
    let i = tabs.indexOf(tab);
    if (event.key === "ArrowLeft") i = (i - 1 + tabs.length) % tabs.length;
    if (event.key === "ArrowRight") i = (i + 1) % tabs.length;
    if (event.key === "Home") i = 0;
    if (event.key === "End") i = tabs.length - 1;
    event.preventDefault();
    tabs[i].focus();
    selectTab(tabs[i]);
  });

  /* Editor: selecting a tab block shows it. */
  document.addEventListener("shopify:block:select", (event) => {
    if (event.target.matches?.("[data-b2b-tabs] [role=tab]")) selectTab(event.target);
  });

  /* ---------------- Sticky inquiry bar (product page) ---------------- */
  let stickyObserver = null;
  const initSticky = () => {
    stickyObserver?.disconnect();
    const cta = $("[data-b2b-main-cta]");
    const bar = $("[data-b2b-sticky-bar]");
    if (!cta || !bar || !("IntersectionObserver" in window)) return;
    stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle("is-visible", show);
        bar.setAttribute("aria-hidden", String(!show));
        $$("button, a", bar).forEach((el) => (el.tabIndex = show ? 0 : -1));
      },
      { threshold: 0 },
    );
    stickyObserver.observe(cta);
  };

  /* ---------------- Hotspots ---------------- */
  const closeHotspots = (except) => {
    $$("[data-b2b-hotspot].is-active").forEach((pin) => {
      if (pin === except) return;
      pin.classList.remove("is-active");
      $(".b2b-hotspot-dot", pin)?.setAttribute("aria-expanded", "false");
      const card = $(".b2b-hotspot-card", pin);
      if (card) card.hidden = true;
    });
  };

  const toggleHotspot = (pin, force) => {
    const open = force ?? !pin.classList.contains("is-active");
    closeHotspots(pin);
    pin.classList.toggle("is-active", open);
    $(".b2b-hotspot-dot", pin)?.setAttribute("aria-expanded", String(open));
    const card = $(".b2b-hotspot-card", pin);
    if (card) card.hidden = !open;
  };

  document.addEventListener("click", (event) => {
    const dot = event.target.closest(".b2b-hotspot-dot");
    if (dot) {
      toggleHotspot(dot.closest("[data-b2b-hotspot]"));
      return;
    }
    const legend = event.target.closest("[data-b2b-hotspot-target]");
    if (legend) {
      const pin = document.getElementById(`Hotspot-${legend.dataset.b2bHotspotTarget}`);
      if (pin) toggleHotspot(pin, true);
      return;
    }
    if (!event.target.closest(".b2b-hotspot-card")) closeHotspots();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeHotspots();
  });

  /* ---------------- Init ---------------- */
  const init = () => {
    if (/[?&]contact_posted=true/.test(window.location.search) || $("[data-b2b-form-errors]") || $("[data-b2b-success]")) {
      handlePosted();
    }
    if (window.location.hash === "#b2b-cart-form") {
      setTimeout(() => document.getElementById("b2b-cart-form")?.scrollIntoView({ block: "start" }), 300);
    }
    initSticky();
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();

  document.addEventListener("shopify:section:load", initSticky);
})();